import { REQUEST_METHOD } from '@/constants';
import { API_ENDPOINTS } from '@/constants/api-endpoints';
import { MAGNET_TYPE } from '@/constants/bot';
import {
  ICreateEntryBody,
  IDashboardScore,
  IEntryDataResponse,
  IInternalAgentSummary,
  ILinksForCTAResponse,
  ITrainData,
  IVendorVendorConfig,
} from '@/types';
import { apiInstance } from '@/utils';
import { isNumber } from 'lodash';

export const getVendorId = async (): Promise<IVendorVendorConfig> => {
  const response = await apiInstance.get(API_ENDPOINTS.getVendorId);
  return response.data; // Returns the full vendor config object
};

export const getDashboardScore = async (
  chatbotToken: string,
  dateParams?: {
    startDate?: Date;
    endDate?: Date;
    preset?: string;
  },
): Promise<IDashboardScore> => {
  const params: Record<string, any> = {
    chat_token: chatbotToken,
  };

  // TEMPORARY FIX: Skip preset parameter due to backend bug
  // The backend returns zeros when preset=30d but rich data without preset
  // TODO: Remove this workaround when backend is fixed
  if (dateParams) {
    // If preset is 'custom' or preset doesn't exist, use start/end dates
    if (
      (dateParams.preset === 'custom' || !dateParams.preset) &&
      dateParams.startDate &&
      dateParams.endDate
    ) {
      params.start_date = dateParams.startDate.toISOString();
      params.end_date = dateParams.endDate.toISOString();
    }
    // TEMPORARILY DISABLED: Skip preset parameter due to backend bug
    // else if (dateParams.preset) {
    //   params.preset = dateParams.preset;
    // }
  }

  const response = await apiInstance({
    url: API_ENDPOINTS.getDashboardScore,
    method: 'GET',
    params,
  });

  // Debug logging (remove in production)
  console.log('Dashboard API called without preset due to backend bug. Data:', {
    conversations: response?.data?.total_conversations,
    campaigns: response?.data?.campaigns?.total,
    visitors: response?.data?.trends?.visitors?.trend?.current_period,
  });

  if (response?.data) {
    const campaigns = response?.data?.campaigns;
    const stats = {
      total_conversations: parseInt(response?.data?.total_conversations, 10),
      avg_messages: parseFloat(response?.data?.avg_messages),
      positive_feedback: parseInt(response?.data?.positive_feedback, 10),
      negative_feedback: parseInt(response?.data?.negative_feedback, 10),
      no_feedback: parseInt(response?.data?.no_feedback, 10),
      positive_feedback_percentage: parseFloat(response?.data?.positive_feedback_percentage),
      negative_feedback_percentage: parseFloat(response?.data?.negative_feedback_percentage),
      campaigns: {
        active: isNumber(campaigns?.active) ? campaigns?.active : 0,
        success_rate: isNumber(campaigns?.success_rate) ? campaigns?.success_rate : 0,
        top_source: campaigns?.top_source,
        total: isNumber(campaigns?.total) ? campaigns?.total : 0,
      },
      trends: response?.data?.trends,
      userEngagementSummary: response?.data?.userEngagementSummary,
    };

    Object.keys(stats).forEach((key) => {
      const statKey = key as keyof typeof stats;
      if (isNaN(stats[statKey] as any) && typeof stats[statKey] !== 'object') {
        stats[statKey] = 0 as any;
      }
    });

    return stats;
  } else {
    throw new Error('No data received from the API');
  }
};

export const getHelpSection = async (token: any) => {
  const response = await apiInstance.get(API_ENDPOINTS.getHelpSection(token));
  return response.data;
};

export const getBotBlog = async (token: string) => {
  const response = await apiInstance.get(API_ENDPOINTS.getBotBlog(token));
  return response.data;
};

export const getBotWebinar = async (token: string) => {
  const response = await apiInstance.get(API_ENDPOINTS.getBotWebinar(token));
  return response.data;
};

export const getDemoDetails = async (token: string) => {
  const response = await apiInstance.get(API_ENDPOINTS.getDemoDetails(token));
  return response.data;
};

export const getBotData = async (token: string) => {
  const response = await apiInstance.get(API_ENDPOINTS.getBotData(token));
  return response.data;
};

export const getLLMSetting = async (token: string) => {
  const response = await apiInstance.get(API_ENDPOINTS.getLLMSetting(token));
  return response.data;
};

export const listPersonas = async (token: string) => {
  const response = await apiInstance.get(API_ENDPOINTS.listPersonas(token));
  return response.data;
};

export const getLinks = async (
  params: any,
): Promise<null | {
  currentPage: number;
  data: any[];
  totalItems: number;
  totalPages: number;
}> => {
  // 0 : Website 1 : SiteMap
  try {
    const res = await apiInstance({
      method: 'GET',
      params,
      url: API_ENDPOINTS.getLinks,
    });

    return {
      ...(res?.data || {}),
      data: res?.data?.data?.map((it: any) => ({
        no: it.link_id,
        url: it.link,
        processed: it.processed,
        created_at: it.created_at,
        refresh_interval_days: it.refresh_interval_days,
        last_refresh_time: it.last_refresh_time,
        next_refresh_time: it.next_refresh_time,
        updated: it.updated,
      })),
    };
  } catch (_error) {
    return null;
  }
};

export const getLinksForCTA = async (params: any): Promise<ILinksForCTAResponse> => {
  const response = await apiInstance({
    method: 'GET',
    url: API_ENDPOINTS.getLinks,
    params,
  });

  return response.data;
};

export const getTrainedData = async (
  params: any,
): Promise<{
  data: ITrainData[];
  pagination: {
    limit: number;
    page: number;
    total: number;
    totalPages: number;
  };
}> => {
  const response = await apiInstance({
    method: 'GET',
    url: API_ENDPOINTS.getTrainedData,
    params,
  });
  return response.data;
};

export const getVideoLinks = async (token: string) => {
  // 0 : Website 1 : SiteMap
  try {
    const res = await apiInstance.get(API_ENDPOINTS.getVideoLinks(token));
    let arr = [];
    for (const link of res?.data) {
      arr.push({
        no: link.link_id,
        url: link.link,
        processed: link.processed, // Add the processed field
        created_at: link.created_at, // You might also want to include the created_at field if needed
      });
    }
    return arr;
  } catch (_error) {
    return null;
  }
};

export const getQA = async (token: string) => {
  const response = await apiInstance.get(API_ENDPOINTS.getQA(token));
  return response.data;
};

export const getThreadDetails = async (token: any, filter: any) => {
  const response = await apiInstance({
    method: 'GET',
    url: API_ENDPOINTS.getThreadDetails(token),
    params: filter,
  });
  return response.data;
};

export const removeTrainedData = async (file_id: number, chatToken: any) => {
  const response = await apiInstance({
    method: 'DELETE',
    data: { chat_token: chatToken },
    url: API_ENDPOINTS.removeTrainedData(file_id),
  });

  return response.data;
};

export const getArchive = async (value: any) => {
  const response = await apiInstance({
    method: 'POST',
    data: { session_id: value },
    url: API_ENDPOINTS.getArchive,
  });

  return response.data;
};

export const getAllBotsData = async (vendorId: number) => {
  console.log('getAllBotsData called with vendorId:', vendorId, 'typeof:', typeof vendorId);

  const response = await apiInstance({
    method: 'GET',
    url: API_ENDPOINTS.getAllBotsData(vendorId),
  });

  return response.data;
};

export const getInternalAgent = async (): Promise<{
  accesstoken: string;
  customizationdata: {
    name: string;
  };
}> => {
  const response = await apiInstance({
    method: 'GET',
    url: API_ENDPOINTS.getInternalAgent,
  });

  return response.data;
};

export const getInternalAgentSummary = async (
  accessToken: string,
): Promise<IInternalAgentSummary> => {
  const response = await apiInstance({
    method: 'GET',
    url: API_ENDPOINTS.getInternalAgentSummary(accessToken),
  });

  return response.data;
};

export const uploadVendorFile = async (
  file: File,
): Promise<{ url: string; cloudflareId: string; fileType: MAGNET_TYPE }> => {
  const formData = new FormData();
  formData.append('file', file);

  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data: formData,
    url: API_ENDPOINTS.uploadVendorFile,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return result.data;
};

export const upsertEntry = async (data: ICreateEntryBody) => {
  const response = await apiInstance({
    method: 'POST',
    url: API_ENDPOINTS.upsertEntry,
    data,
  });

  return response.data;
};

export const getEntries = async (chatToken: string): Promise<IEntryDataResponse> => {
  const response = await apiInstance({
    method: 'GET',
    url: API_ENDPOINTS.getEntries(chatToken),
  });

  return response.data;
};

export const createDefaultBot = async (accessToken?: string) => {
  const response = await fetch(API_ENDPOINTS.createDefaultBot, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  return response.json();
};

/**
 * Converts a discovered link to a link for training
 * @param linkId The ID of the discovered link to convert
 * @param chatToken The chat token
 * @returns Promise with the updated link data
 */
export const convertDiscoveredLink = async (linkId: number, chatToken: string) => {
  const response = await apiInstance({
    method: 'POST',
    url: API_ENDPOINTS.convertDiscoveredLink(linkId),
    params: {
      chat_token: chatToken,
    },
  });

  return response.data;
};

export const improveAnswer = async (data: any) => {
  const response = await apiInstance({
    method: 'POST',
    url: API_ENDPOINTS.improveAnswer,
    data,
  });

  return response.data;
};
